import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  API_BASE,
  api,
  qs,
  type LicenceRow,
  type SushiKey,
  type UsageDay,
  type UsageReport,
} from '../lib/api'
import { Banner, Empty, ErrorNote } from '../components/Page'
import { Card, CardBar, ConsoleHead, StatCell } from '../components/admin/Console'
import { useSession } from '../lib/session'

/**
 * The window the report covers: the last thirty days.
 *
 * A calendar month is what a COUNTER report is filed against, and the exports
 * below carry whatever window is on screen. Thirty days rolling is what an
 * administrator asking "are we short of seats" actually wants to see, because
 * on the second of the month a calendar month is two days of evidence.
 */
function lastThirtyDays(): { from: string; to: string; label: string } {
  const now = new Date()
  const start = new Date(now)
  start.setDate(start.getDate() - 29)
  const iso = (date: Date) => date.toISOString().slice(0, 10)
  const day = (date: Date) =>
    date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  return { from: iso(start), to: iso(now), label: `${day(start)} to ${day(now)}`.toUpperCase() }
}

/**
 * Usage reporting, with export.
 *
 * Peak concurrency is recorded deliberately: it is the number a library uses
 * to decide whether to buy another seat, and a denial is a reader who was
 * turned away. Both are counts of people, so both sit on one axis; the chart
 * has no second scale and nothing here is rounded for presentation.
 */
export function UsagePage() {
  const { config } = useSession()
  const window = useMemo(lastThirtyDays, [])
  const [modelled, setModelled] = useState(false)
  const [licenceId, setLicenceId] = useState<string | null>(null)

  const licences = useQuery({
    queryKey: ['admin', 'licences'],
    queryFn: () => api.get<LicenceRow[]>('/admin/licences'),
  })
  const licence = licences.data?.find((row) => row.id === licenceId) ?? licences.data?.[0]

  const report = useQuery({
    queryKey: ['admin', 'usage', licence?.id, window.from, window.to],
    queryFn: () =>
      api.get<UsageReport>(
        `/admin/usage${qs({ licence_id: licence?.id, from: window.from, to: window.to })}`,
      ),
    enabled: !!licence,
    retry: false,
  })

  const sushiKey = useMutation({
    mutationFn: (institutionId: string) =>
      api.post<SushiKey>('/admin/usage/sushi-key', { institution_id: institutionId }),
  })
  const exportHref = (format: string) =>
    `${API_BASE}/admin/usage${qs({ licence_id: licence?.id, from: window.from, to: window.to, format })}`

  if (report.isError) {
    return (
      <>
        <ConsoleHead title="Usage reporting" />
        <Card>
          <Empty>
            The usage endpoint is not in this build yet. It answers at{' '}
            <span className="font-mono text-[13px]">/api/admin/usage</span> with a row per day of
            sessions, peak concurrency, denials and mean session length, counted from the audit log
            and the content-session table, and with{' '}
            <span className="font-mono text-[13px]">?format=csv</span> and{' '}
            <span className="font-mono text-[13px]">?format=counter</span> for the library&apos;s own
            reporting. Until then, live pressure on the user limit is on the Sessions screen.
          </Empty>
        </Card>
        <ErrorNote error={report.error} />
      </>
    )
  }

  const days = report.data?.days ?? []
  const totals = report.data?.totals
  const seatLimit = licence?.concurrent_limit ?? licence?.named_seat_limit ?? null
  const daysAtLimit = totals?.days_at_limit ?? 0
  const denials = totals?.denials ?? days.reduce((sum, day) => sum + day.denials, 0)
  // Days at the limit and days that turned somebody away are different
  // counts, and conflating them overstates the case for another seat.
  const daysWithDenials = days.filter((day) => day.denials > 0).length

  return (
    <>
      <ConsoleHead
        title="Usage reporting"
        actions={
          (licences.data?.length ?? 0) > 1 ? (
            <select
              className="input-dense w-auto"
              aria-label="Licence"
              value={licence?.id ?? ''}
              onChange={(event) => setLicenceId(event.target.value)}
            >
              {licences.data?.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.institution_name} × {row.publication_name}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />

      <Card className="max-w-[900px]">
        <CardBar>
          <div>
            <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-ink2">
              {licence?.publication_name ?? 'no licence'} · {window.label}
            </div>
            <h2 className="text-[20px] font-semibold leading-[1.15]">Licence pressure and denials</h2>
          </div>
          <div className="ml-auto flex flex-wrap gap-[7px]">
            <a
              className="btn-secondary px-[11px] py-[5px] text-[13px] no-underline"
              href={exportHref('counter')}
            >
              COUNTER R5
            </a>
            <button
              type="button"
              className="btn-secondary px-[11px] py-[5px] text-[13px]"
              disabled={!licence || sushiKey.isPending}
              title="Issue the SUSHI key this licence's institution harvests the COUNTER report with. Shown once; issuing again replaces it."
              onClick={() => licence && sushiKey.mutate(licence.institution_id)}
            >
              {sushiKey.isPending ? 'Issuing…' : 'SUSHI key'}
            </button>
            <a
              className="btn-secondary px-[11px] py-[5px] text-[13px] no-underline"
              href={exportHref('csv')}
            >
              CSV
            </a>
          </div>
        </CardBar>

        {sushiKey.data && (
          <div className="px-5 pt-4">
            <Banner tone="success">
              SUSHI key issued for requestor <code>{sushiKey.data.requestor_id}</code>. It is
              shown once and stored as a hash; issuing again replaces it. Key:{' '}
              <code>{sushiKey.data.api_key}</code>. Harvest with{' '}
              <code>
                {sushiKey.data.endpoint}?customer_id={sushiKey.data.customer_id}&amp;api_key=
                {sushiKey.data.api_key}&amp;begin_date=YYYY-MM&amp;end_date=YYYY-MM
              </code>
            </Banner>
          </div>
        )}
        <ErrorNote error={sushiKey.error} />

        {report.isLoading ? (
          <Empty>Counting the month.</Empty>
        ) : !days.length ? (
          <Empty>
            Nothing has been read under this licence between {window.from} and {window.to}. A row
            appears here for every day a reader opens a session, and the peak concurrency it records
            is what a user limit is argued from.
          </Empty>
        ) : (
          <>
            <div className="p-[18px]">
              <UsageChart days={days} modelled={modelled} />
            </div>

            <div className="grid grid-cols-2 border-t border-n300 md:grid-cols-5">
              <StatCell label="Sessions" value={(totals?.sessions ?? 0).toLocaleString()} />
              <StatCell
                label="Peak concurrency"
                value={`${totals?.peak_concurrency ?? 0} / ${seatLimit ?? 'none'}`}
              />
              <StatCell label="Denials" value={denials} tone={denials ? 'mark' : undefined} />
              <StatCell
                label="Days at limit"
                value={daysAtLimit}
                tone={daysAtLimit ? 'mark' : undefined}
              />
              <StatCell
                label="Mean session"
                value={
                  totals?.mean_session_s ? `${Math.round(totals.mean_session_s / 60)} min` : 'none'
                }
              />
            </div>

            <div
              className="border-t border-n300 px-[18px] py-[13px]"
              style={
                denials
                  ? {
                      background: 'var(--color-accent-2-100)',
                      boxShadow: 'inset 3px 0 0 var(--color-accent-2-700)',
                    }
                  : {
                      background: 'var(--color-surface)',
                      boxShadow: 'inset 3px 0 0 var(--color-neutral-500)',
                    }
              }
            >
              <div className="text-[15px] leading-[1.5]">
                {denials ? (
                  <>
                    On {daysWithDenials} {daysWithDenials === 1 ? 'day' : 'days'} in this window a
                    reader was turned away because{' '}
                    {seatLimit === 1 ? 'the only place was' : 'every place was'} in use, {denials}{' '}
                    {denials === 1 ? 'time' : 'times'} in all. Every place was taken on{' '}
                    {daysAtLimit} of the {days.length} days.
                    {modelled && (
                      <>
                        {' '}
                        One more user on the limit clears a denial whenever no more than one reader is waiting at
                        that moment. The daily rows cannot say how many were simultaneous, so{' '}
                        {denials} is the ceiling and {daysWithDenials} the floor.
                      </>
                    )}
                  </>
                ) : (
                  <>
                    No reader was turned away in this window. Peak concurrency reached{' '}
                    {totals?.peak_concurrency ?? 0} against {seatLimit ?? 'an unlimited number of'}{' '}
                    {seatLimit === 1 ? 'user' : 'users'}, so the licence is not under pressure.
                  </>
                )}
              </div>
              <div className="mt-[11px] flex flex-wrap gap-[7px]">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!denials}
                  onClick={() => setModelled((value) => !value)}
                >
                  {modelled ? 'Hide the model' : 'Model one more user'}
                </button>
                <a
                  className="btn-secondary bg-paper no-underline"
                  href={acquisitionsMail(config?.support_email, licence, window.label, {
                    sessions: totals?.sessions ?? 0,
                    peak: totals?.peak_concurrency ?? 0,
                    denials,
                    daysAtLimit,
                  })}
                >
                  Send to acquisitions
                </a>
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  )
}

function acquisitionsMail(
  to: string | undefined,
  licence: LicenceRow | undefined,
  period: string,
  figures: { sessions: number; peak: number; denials: number; daysAtLimit: number },
): string {
  const subject = `Licence pressure: ${licence?.publication_name ?? 'licence'}, ${period}`
  const body = [
    `Licence: ${licence?.institution_name ?? ''} for ${licence?.publication_name ?? ''}`,
    `Period: ${period}`,
    `Sessions: ${figures.sessions}`,
    `Peak concurrency: ${figures.peak} of ${licence?.concurrent_limit ?? 'unlimited'}`,
    `Denials: ${figures.denials}`,
    `Days at the limit: ${figures.daysAtLimit}`,
    '',
    'Every figure above is counted from the platform usage report and is exportable as COUNTER R5 or CSV.',
  ].join('\n')
  return `mailto:${to ?? ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/**
 * The daily chart.
 *
 * One axis, counts of readers, shared by both marks: concurrent readers who
 * got in, and readers turned away. They are drawn as one column per day with a
 * paper gap between the two segments, so the pair reads as the day's demand
 * without either measure being scaled against the other. The accent and the
 * magenta are close in lightness, so position, the gap and the legend carry
 * the identity rather than the hue alone.
 */
function UsageChart({ days, modelled }: { days: UsageDay[]; modelled: boolean }) {
  const height = 116
  const gap = 2
  const columns = days.length
  const width = Math.max(columns * 12, 320)
  const step = width / Math.max(columns, 1)
  const barWidth = Math.max(step - gap, 2)
  const ceiling = Math.max(1, ...days.map((day) => day.peak_concurrency + day.denials))
  const unit = (height - 8) / ceiling

  return (
    <div>
      <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">
        readers · {ceiling} at the top of the axis
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="block h-[116px] w-full"
        role="img"
        aria-label={`Daily peak concurrency and denials for ${columns} days`}
      >
        {days.map((day, index) => {
          const x = index * step
          const peakHeight = day.peak_concurrency > 0 ? day.peak_concurrency * unit : 3
          const denialHeight = day.denials * unit
          const closed = day.peak_concurrency === 0 && day.sessions === 0
          return (
            <g key={day.date}>
              <title>
                {day.date}: {day.sessions} sessions, peak {day.peak_concurrency}, {day.denials}{' '}
                denials
              </title>
              <rect
                x={x}
                y={height - peakHeight}
                width={barWidth}
                height={peakHeight}
                fill={closed ? 'var(--color-neutral-300)' : 'var(--jl-accent)'}
              />
              {day.denials > 0 && (
                <rect
                  x={x}
                  y={height - peakHeight - gap - denialHeight}
                  width={barWidth}
                  height={denialHeight}
                  fill="var(--color-accent-2-700)"
                  opacity={modelled ? 0.35 : 1}
                />
              )}
              {modelled && day.denials > 0 && (
                <rect
                  x={x}
                  y={height - peakHeight - gap - denialHeight}
                  width={barWidth}
                  height={denialHeight}
                  fill="none"
                  stroke="var(--color-accent-2-700)"
                  strokeWidth={1}
                  strokeDasharray="2 2"
                />
              )}
            </g>
          )
        })}
        <line x1={0} y1={height} x2={width} y2={height} stroke="var(--color-neutral-400)" strokeWidth={1} />
      </svg>

      <div className="mt-[7px] flex justify-between font-mono text-[10px] uppercase text-ink2">
        <div>{label(days[0]?.date)}</div>
        {columns > 2 && <div>{label(days[Math.floor(columns / 2)]?.date)}</div>}
        <div>{label(days[columns - 1]?.date)}</div>
      </div>

      <div className="mt-[11px] flex flex-wrap gap-[18px]">
        {[
          ['var(--jl-accent)', 'Peak concurrency · readers at once'],
          ['var(--color-accent-2-700)', 'Denials · reader turned away'],
          ['var(--color-neutral-300)', 'Closed · no sessions'],
        ].map(([colour, text]) => (
          <div key={text} className="flex items-center gap-[7px] text-[13px] text-ink2">
            <span className="h-[9px] w-4 flex-none" style={{ background: colour }} />
            {text}
          </div>
        ))}
      </div>
    </div>
  )
}

function label(iso: string | undefined): string {
  if (!iso) return ''
  const date = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}
